import React, { useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import { bool } from 'prop-types';
import { useSelector } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';
import { getDeviceType as getDeviceTypeSelector } from '../../selectors/CMPageSelector';
import { commonAutomationIds } from '../CoreMediaPage/CMPage.config';
import CatchAllWrapper from './CatchAllWrapper';

const ImageHotSpot = (props) => {
    const data = get(props, 'data', props);
    const { isCMPreview } = props;
    const { automationId } = data;
    const deviceType = useSelector((state) => getDeviceTypeSelector(state));
    const [smComponentData, setSmComponentData] = useState({});
    const [mdComponentData, setMdComponentData] = useState({});
    const [lgComponentData, setLgComponentData] = useState({});

    const imageWrapperRef = useRef(null);
    const image = useRef(null);
    const imagemap = useRef(null);

    const generateComponentData = () => {
        data?.pictureVariants?.forEach((currentVariant) => {
            const {
                variant,
                target: { picture, hotZones },
            } = currentVariant;
            switch (variant) {
                case 'S':
                    const smImageURL = picture?.fullyQualifiedUrl;
                    const smAltText = picture?.alt;
                    const useMapNameSm = 'small-imagehotspot-component';
                    setSmComponentData({ hotZones, smImageURL, smAltText, useMapNameSm });
                    break;
                case 'M':
                    const mdImageURL = picture?.fullyQualifiedUrl;
                    const mediumAltText = picture?.alt;
                    const useMapNameMd = 'medium-imagehotspot-component';
                    setMdComponentData({ hotZones, mdImageURL, mediumAltText, useMapNameMd });
                    break;
                case 'XL':
                    const lgImageURL = picture?.fullyQualifiedUrl;
                    const lgAltText = picture?.alt;
                    const useMapNameLg = 'large-imagehotspot-component';
                    setLgComponentData({ hotZones, lgImageURL, lgAltText, useMapNameLg });
                    break;
            }
        });
    };

    const handleImageLoaded = () => {
        if (!__SERVER__) {
            const { naturalWidth } = image?.current || {};
            const { naturalHeight } = image?.current || {};
            const areas = (imagemap?.current || {}).children;
            const areaLength = (areas || {}).length;
            const imageWrapper = imageWrapperRef?.current || {};
            const coords = [];
            const scaleWidth = imageWrapper.clientWidth / naturalWidth;
            const scaleHeight = imageWrapper.clientHeight / naturalHeight;
            let iteratorX = 0;
            let iteratorY = 0;
            let selScaeUnit = 0;
            let currentCoords = lgComponentData?.hotZones;

            if (deviceType?.isMobile) {
                currentCoords = smComponentData?.hotZones;
            } else if (deviceType?.isTablet) {
                currentCoords = mdComponentData?.hotZones;
            } else if (deviceType?.isDesktop) {
                currentCoords = lgComponentData?.hotZones;
            } else {
                currentCoords = lgComponentData?.hotZones;
            }

            for (iteratorX = 0; iteratorX < areaLength; iteratorX += 1) {
                /* getting the actual map area co-ordinates */
                coords[iteratorX] = currentCoords?.[iteratorX]?.coords?.split(',');
                for (iteratorY = 0; iteratorY < coords[iteratorX]?.length; iteratorY += 1) {
                    selScaeUnit = iteratorY % 2 ? scaleHeight : scaleWidth;
                    /* multiplying the actual map area co-ordinates with scaleUnit */
                    coords[iteratorX][iteratorY] *= selScaeUnit;
                }
                /* updating the DOM */
                areas[iteratorX].coords = coords?.[iteratorX]?.join(',');
            }
        }
    };

    useEffect(() => {
        generateComponentData();
        window?.addEventListener('resize', handleImageLoaded());

        return () => {
            window?.removeEventListener('resize', handleImageLoaded());
        };
    }, []);

    useEffect(() => {
        generateComponentData();
    }, [data, data?.pictureVariants]);

    const renderHotZones = (hotSpotZones) => {
        const renderHotSpotZones = [];
        hotSpotZones?.forEach((hotZone) => {
            const {
                coords,
                linkedContent: {
                    altText,
                    analyticsId,
                    linkText,
                    urlHolder: { openInNewTab, type, value },
                },
            } = hotZone;
            const linkTarget = openInNewTab ? '_blank' : '';
            renderHotSpotZones?.push(
                <area
                    shape="rect"
                    coords={coords}
                    data-soft-link={value}
                    data-leaf="true"
                    data-analytics-tag={analyticsId}
                    alt={altText}
                    title={altText}
                    target={linkTarget}
                    href={value}
                />
            );
        });
        return renderHotSpotZones;
    };

    const bannerImage = (hotZones, imgURl, altText, mapName) => {
        const linkTarget = data?.catchAllTarget?.urlHolder?.openInNewTab ? '_blank' : '';
        return (
            <div
                data-cm-metadata={isCMPreview && metaDataElement(data)}
                ref={imageWrapperRef}
                data-automation-id={automationId}
            >
                <CatchAllWrapper
                    condition={
                        data?.catchAllTarget?.urlHolder?.value !== '' && data?.catchAllTarget
                    }
                    wrapper={(children) => (
                        <a
                            href={data?.catchAllTarget?.urlHolder?.value}
                            title={data?.catchAllTarget?.altText}
                            target={linkTarget}
                        >
                            {children}
                        </a>
                    )}
                >
                    <img
                        className={dt(['w-full', 'h-[auto]'])}
                        alt={altText}
                        src={imgURl}
                        data-cm-metadata={isCMPreview && metaDataProperty(data?.name)}
                        useMap={`#${mapName} ${data?.name}`}
                        ref={image}
                        onLoad={() => handleImageLoaded()}
                        data-automation-id={commonAutomationIds?.IMAGE}
                    />
                </CatchAllWrapper>
                <map
                    ref={imagemap}
                    name={`${mapName} ${data?.name}`}
                    data-automation-id="at-hotzones"
                >
                    {renderHotZones(hotZones)}
                </map>
            </div>
        );
    };

    const imageHotSpotComponent = () => {
        const { lgImageURL, lgAltText, useMapNameLg } = lgComponentData;
        const { mdImageURL, mediumAltText, useMapNameMd } = mdComponentData;
        const { smImageURL, smAltText, useMapNameSm } = smComponentData;

        let hotSpotComponent = null;
        if (deviceType) {
            if (deviceType?.isMobile) {
                hotSpotComponent = bannerImage(
                    smComponentData?.hotZones,
                    smImageURL,
                    smAltText,
                    useMapNameSm
                );
            } else if (deviceType?.isTablet) {
                hotSpotComponent = bannerImage(
                    mdComponentData?.hotZones,
                    mdImageURL,
                    mediumAltText,
                    useMapNameMd
                );
            } else if (deviceType?.isDesktop) {
                hotSpotComponent = bannerImage(
                    lgComponentData?.hotZones,
                    lgImageURL,
                    lgAltText,
                    useMapNameLg
                );
            } else {
                hotSpotComponent = bannerImage(
                    lgComponentData?.hotZones,
                    lgImageURL,
                    lgAltText,
                    useMapNameLg
                );
            }
        }
        return hotSpotComponent;
    };

    return <>{imageHotSpotComponent()}</>;
};

ImageHotSpot.defaultProps = {
    isCMPreview: false,
};

ImageHotSpot.propTypes = {
    isCMPreview: bool,
};

export default ImageHotSpot;
